/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip, Button, Col, Select, Modal } from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { parseISO } from "date-fns";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Filters, FiltersCard } from "../../components/Filters";
import { Label } from "../../components/ui";

import api from "../../services/api";

import { useToast } from "../../hooks/toast";
import { useAuth } from "../../hooks/auth";
// eslint-disable-next-line import/extensions
import Table from "../../components/Table";
import EquipmentModal, { IEquipmentTypes } from "./EquipmentModal";
import { Container } from "./styles";
import SendMessageModal from "./SendMessageModal";
import UserRoles from "../../enums/UserRoles";
import UserAvatar from "../../components/UserAvatar";

import { IUser } from "../User";
import filterOptions from "../../utils/filterOptions";

const { confirm } = Modal;

export interface IEquipment {
  id: string;
  equipment_id: string;
  equipment_type: string;
  date_acquisition: string;
  status: "FREE" | "DEFECT" | "IN USE";
  description_defect: string | null;
  assignment: string | null;
  equipment_description: string;
  user_id: string | null;
  user: {
    avatar: string | null;
  };
}

const Equipments: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [equipments, setEquipments] = useState<IEquipment[]>([]);
  const [equipmentsTypes, setEquipmentsTypes] = useState<IEquipmentTypes[]>([]);
  const [managers, setManagers] = useState<IUser[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [sendMessageModalVisible, setSendMessageModalVisible] = useState<
    boolean
  >(false);
  const [editedEquipment, setEditedEquipment] = useState<
    IEquipment | undefined
  >();
  const [heritageId, setHeritageId] = useState<string | undefined>(undefined);
  const { addToast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation(["consultants"]);

  // Filters
  const [status, setStatus] = useState();
  const [type, setType] = useState();
  const [manager, setManager] = useState();

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role],
  );
  const isLoggedUserManagerOrTechician = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role],
  );

  const searchParams = useMemo(() => {
    return {
      status,
      type,
      manager,
      heritage_id: heritageId,
    };
  }, [status, type, manager, heritageId]);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined, isDeleting?: boolean) => {
      let description;

      switch (response?.status) {
        case 403:
          description = t("requestError.403");
          break;
        case 401:
          description = t("requestError.401");
          break;
        case 404:
          description = t("requestError.404");
          break;
        default:
          description = isDeleting
            ? t("requestError.delete")
            : t("requestError.get");
      }

      addToast({
        type: "error",
        title: isDeleting ? t("deleteStatus") : t("getStatus"),
        description,
      });
    },
    [addToast, t],
  );

  const handleCloseModal = useCallback(
    (user?: IEquipment[]) => {
      setEditedEquipment(undefined);
      if (user) {
        setEquipments(user);
      }
      setModalVisible(false);
      setSendMessageModalVisible(false);
    },
    [equipments],
  );

  useEffect(() => {
    api
      .get("/equipments/types")
      .then(response => {
        setEquipmentsTypes(response.data);
      })
      .catch(err => {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get<IEquipment[]>("/equipments", {
        params: searchParams,
      })
      .then(response => {
        setEquipments(response.data);
      })
      .catch(err => {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setEditedEquipment]);
  useEffect(() => {
    setLoading(true);
    api
      .get<IUser[]>("/users")
      .then(response => {
        setManagers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro ao buscar os equipamentos",
          description:
            "Occoreu um erro ao buscar os equipamentos, tente novamente.",
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const equipmentsOption = useMemo(() => {
    return equipments
      .filter(equipment => equipment.equipment_id)
      .map(equipment => {
        return {
          label: equipment.equipment_id,
          value: equipment.equipment_id,
        };
      });
  }, [equipments]);

  const handleDeleteEquipment = useCallback(
    (equipmentId: string) => {
      api
        .delete(`/equipments/${equipmentId}`)
        .then(() => {
          addToast({
            type: "success",
            title: t("requestEq.delete"),
            description: t("requestEq.deleteDescription"),
          });

          setEquipments(oldEquipment => [
            ...oldEquipment.filter(equi => equi.id !== equipmentId),
          ]);
        })
        .catch(err => {
          if (axios.isAxiosError(err)) {
            const serverError = err as AxiosError;
            generateErrorToast(serverError.response);
          }
        });
    },
    [addToast, generateErrorToast],
  );

  const handleEditUser = useCallback((user: IEquipment) => {
    setEditedEquipment(user);
    setModalVisible(true);
  }, []);

  const showDelConfirm = useCallback(
    (equipmentId, equipmentType) => {
      confirm({
        title: t("equipments.deleteModal.title"),
        icon: <ExclamationCircleOutlined />,
        content: t("equipments.deleteModal.alert", { name: equipmentType }),
        centered: true,
        okText: t("equipments.deleteModal.yes"),
        cancelText: t("equipments.deleteModal.no"),
        okButtonProps: { type: "primary", danger: true },
        onOk: () => handleDeleteEquipment(equipmentId),
      });
    },
    [t, handleDeleteEquipment],
  );

  const columns: ColumnsType<IEquipment> = [
    {
      title: t("equipments.equipment_type"),
      dataIndex: "equipment_type",
    },
    {
      title: t("equipments.equipment_id"),
      dataIndex: "equipment_id",
    },
    {
      title: t("equipments.equipment_acquisition"),
      dataIndex: "date_acquisition",
      render: (date_acquisition: string) => {
        return parseISO(date_acquisition.split("T")[0]).toLocaleDateString(
          "pt-BR",
        );
      },
    },
    {
      title: t("equipments.status"),
      dataIndex: "status",
      render: (status: string) => {
        return t(`equipments.${status !== "IN USE" ? status : "IN_USE"}`);
      },
    },
    {
      title: t("equipments.description_defect"),
      dataIndex: "description_defect",
      render: (description_defect: string) => {
        return description_defect || "";
      },
    },
    {
      title: t("equipments.assignment"),
      dataIndex: "assignment",
      render: (assignment: string, record) => {
        return assignment ? (
          <UserAvatar
            name={assignment}
            url={record?.user?.avatar || ""}
            size={36}
          />
        ) : (
          <></>
        );
      },
    },
    {
      title: t("equipments.equipment_description"),
      dataIndex: "equipment_description",
    } /*
    {
      title: t("equipments.user_id"),
      dataIndex: "user_id",
      render: (user_id: string) => {
        return user_id || "";
      },
    }, */,
    {
      title: "Ações",
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => {
        return (
          <>
            {isLoggedUserManager ? (
              <>
                <Button
                  icon={<EditOutlined />}
                  type="link"
                  onClick={() => handleEditUser(record)}
                />
                <Button
                  icon={<DeleteOutlined />}
                  type="link"
                  danger
                  onClick={
                    () => showDelConfirm(record.id, record.equipment_type)
                    // ts-ignore
                  }
                />
              </>
            ) : (
              <></>
            )}
          </>
        );
      },
    },
  ];

  const handleShowFilters = useCallback(
    value => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showFilters) {
        setStatus(undefined);
        setType(undefined);
        setManager(undefined);
        setHeritageId(undefined);
      }
    },
    [showFilters],
  );

  const extraActions = useMemo(
    () =>
      isLoggedUserManagerOrTechician ? (
        <Tooltip title={t("actions.create")}>
          <Button
            icon={<PlusCircleOutlined />}
            type="link"
            size="large"
            onClick={() => setModalVisible(true)}
          />
        </Tooltip>
      ) : (
        <></>
      ),
    [isLoggedUserManager, t],
  );

  const equipmentStatusOptions = useMemo(() => {
    return [
      {
        label: t("equipments.FREE"),
        value: "FREE",
      },
      {
        label: t("equipments.IN_USE"),
        value: "IN USE",
      },
      {
        label: t("equipments.DEFECT"),
        value: "DEFECT",
      },
    ];
  }, [t]);
  const managerOptions = useMemo(() => {
    if (!managers) {
      return undefined;
    }

    return (
      managers
        // eslint-disable-next-line no-nested-ternary
        .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
        .map(mng => ({
          label: mng.name,
          value: mng.id,
        }))
    );
  }, [managers]);

  const equipmentTypeOptions = useMemo(() => {
    if (!equipmentsTypes.length) {
      return undefined;
    }

    return equipmentsTypes.map(equipmentType => ({
      label: t(`equipments.${equipmentType.name}`),
      value: equipmentType.name,
    }));
  }, [t, equipmentsTypes]);

  const extraFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("equipments.status")}</Label>
            </div>
            <Select
              placeholder={t("equipments.status")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              onChange={value => setStatus(value)}
              filterOption={filterOptions}
              options={equipmentStatusOptions}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("equipments.equipment_type")}</Label>
            </div>
            <Select
              placeholder={t("equipments.equipment_type")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              onChange={value => setType(value)}
              filterOption={filterOptions}
              options={equipmentTypeOptions}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("equipments.manager")}</Label>
            </div>
            <Select
              placeholder={t("equipments.manager")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              onChange={value => setManager(value)}
              options={managerOptions}
              filterOption={filterOptions}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("equipments.equipment_id")}</Label>
            </div>
            <Select
              placeholder={t("equipments.equipment_id")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              onChange={value => setHeritageId(value)}
              options={equipmentsOption}
              filterOption={filterOptions}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [showFilters, t],
  );

  return (
    <Container>
      <EquipmentModal
        visible={modalVisible}
        handleCloseModal={handleCloseModal}
        equipment={editedEquipment}
      />
      <SendMessageModal
        visible={sendMessageModalVisible}
        handleCloseModal={handleCloseModal}
        user={editedEquipment}
      />
      <Table
        data={equipments}
        columns={columns}
        title={
          <>
            {t("equipments.title")}
            <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            />
          </>
        }
        extraActions={extraActions}
        footer={() => <></>}
        loading={loading}
        pagination={false}
        extraFilters={extraFilters}
      />
    </Container>
  );
};

export default Equipments;
